import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmptyState } from '../components/EmptyState'
import { InlineNotice } from '../components/InlineNotice'
import { LoadingBlock } from '../components/LoadingBlock'
import { Modal } from '../components/Modal'
import { StatusBadge } from '../components/StatusBadge'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { clientDisplayName, formatDate, formatMoney, serviceName, sourceName } from '../lib/format'
import { errorMessage } from '../lib/errors'
import { consumePendingAction } from '../lib/pendingAction'
import { supabase } from '../lib/supabase'
import type { ClientRow, OrderRow, SimpleProfile, SimpleService, SimpleSource } from '../lib/types'

const confirmationLabels = {
  pl: { draft: 'Szkic', confirmed: 'Potwierdzone', cancelled: 'Anulowane' },
  ru: { draft: 'Черновик', confirmed: 'Подтверждён', cancelled: 'Отменён' }
} as const
const paymentLabels = {
  pl: { unpaid: 'Nieopłacone', partially_paid: 'Częściowo opłacone', paid: 'Opłacone', overdue: 'Przeterminowane', refunded: 'Zwrócone' },
  ru: { unpaid: 'Не оплачено', partially_paid: 'Оплачено частично', paid: 'Оплачено', overdue: 'Просрочено', refunded: 'Возвращено' }
} as const
const fulfilmentLabels = {
  pl: { to_plan: 'Do zaplanowania', to_purchase: 'Do zakupu', in_production: 'W realizacji', ready: 'Gotowe', waiting_for_pickup: 'Oczekuje na odbiór', in_delivery: 'W dostawie', delivered: 'Dostarczone', completed: 'Zakończone', complaint: 'Reklamacja' },
  ru: { to_plan: 'Нужно запланировать', to_purchase: 'Нужна закупка', in_production: 'В производстве', ready: 'Готово', waiting_for_pickup: 'Ожидает самовывоза', in_delivery: 'В доставке', delivered: 'Доставлено', completed: 'Завершено', complaint: 'Рекламация' }
} as const

type ClientMode = 'existing' | 'new'
type OrderForm = {
  client_mode: ClientMode
  client_id: string
  new_client_type: 'individual' | 'company'
  new_client_first_name: string
  new_client_last_name: string
  new_client_company_name: string
  new_client_phone: string
  new_client_email: string
  new_client_instagram: string
  source_id: string
  service_id: string
  responsible_user_id: string
  title: string
  fulfilment_at: string
  amount_gross: string
  vat_rate: string
  delivery_method: 'pickup' | 'delivery' | 'onsite'
  recipient_name: string
  recipient_phone: string
  delivery_address: string
  confirmation_status: 'draft' | 'confirmed'
  payment_status: 'unpaid' | 'partially_paid' | 'paid'
  notes: string
}

function localTomorrowAtNine() {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  date.setHours(9, 0, 0, 0)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

const emptyForm = (): OrderForm => ({
  client_mode: 'existing',
  client_id: '',
  new_client_type: 'individual',
  new_client_first_name: '',
  new_client_last_name: '',
  new_client_company_name: '',
  new_client_phone: '',
  new_client_email: '',
  new_client_instagram: '',
  source_id: '',
  service_id: '',
  responsible_user_id: '',
  title: '',
  fulfilment_at: localTomorrowAtNine(),
  amount_gross: '',
  vat_rate: '23',
  delivery_method: 'pickup',
  recipient_name: '',
  recipient_phone: '',
  delivery_address: '',
  confirmation_status: 'confirmed',
  payment_status: 'unpaid',
  notes: ''
})

export function OrdersPage() {
  const { t, language } = useLanguage()
  const { profile } = useAuth()
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [clients, setClients] = useState<ClientRow[]>([])
  const [sources, setSources] = useState<SimpleSource[]>([])
  const [services, setServices] = useState<SimpleService[]>([])
  const [profiles, setProfiles] = useState<SimpleProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [form, setForm] = useState<OrderForm>(emptyForm())

  const canDelete = Boolean(profile?.is_system_admin || profile?.role === 'owner' || profile?.role === 'manager')

  const load = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    setError(null)
    const [orderResult, clientResult, sourceResult, serviceResult, profileResult] = await Promise.all([
      supabase.from('orders').select(`
        *,
        clients(id,client_type,first_name,last_name,company_name,instagram_handle,email,phone),
        services(id,code,name_pl,name_ru),
        responsible:profiles!orders_responsible_user_id_fkey(id,full_name,email,role)
      `).order('created_at', { ascending: false }),
      supabase.from('clients').select('*').eq('active', true).order('updated_at', { ascending: false }),
      supabase.from('lead_sources').select('id,code,name_pl,name_ru').eq('active', true).order('name_pl'),
      supabase.from('services').select('id,code,name_pl,name_ru,kind,active').eq('active', true).eq('kind', 'primary').order('name_pl'),
      supabase.from('profiles').select('id,full_name,email,role,active').eq('active', true).order('full_name')
    ])
    const firstError = orderResult.error || clientResult.error || sourceResult.error || serviceResult.error || profileResult.error
    if (firstError) setError(firstError.message)
    else {
      setOrders((orderResult.data || []) as unknown as OrderRow[])
      setClients((clientResult.data || []) as ClientRow[])
      setSources((sourceResult.data || []) as SimpleSource[])
      setServices((serviceResult.data || []) as SimpleService[])
      setProfiles((profileResult.data || []) as SimpleProfile[])
    }
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const open = () => openCreate()
    if (consumePendingAction('new-order')) openCreate()
    window.addEventListener('sgflow:new-order', open)
    return () => window.removeEventListener('sgflow:new-order', open)
  }, [profiles.length])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return orders
    return orders.filter((order) => [order.number, order.title, clientDisplayName(order.clients), order.services?.name_pl, order.services?.name_ru]
      .filter(Boolean).join(' ').toLowerCase().includes(query))
  }, [orders, search])

  function openCreate() {
    const next = emptyForm()
    if (profiles.length === 1) next.responsible_user_id = profiles[0].id
    setForm(next)
    setError(null)
    setNotice(null)
    setModalOpen(true)
  }

  function update<K extends keyof OrderForm>(key: K, value: OrderForm[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    if (!supabase) return
    if (!form.service_id || !form.responsible_user_id || !form.fulfilment_at || !form.amount_gross) {
      setError(t('requiredFields'))
      return
    }
    if (form.client_mode === 'existing' && !form.client_id) {
      setError(t('chooseClient'))
      return
    }
    if (form.client_mode === 'new') {
      const missingName = form.new_client_type === 'company'
        ? !form.new_client_company_name.trim()
        : !form.new_client_first_name.trim()
      if (missingName || !form.new_client_phone.trim()) {
        setError(t('quickClientRequired'))
        return
      }
    }
    if (form.delivery_method === 'delivery' && (!form.delivery_address.trim() || !form.recipient_name.trim() || !form.recipient_phone.trim())) {
      setError(t('recipientRequired'))
      return
    }

    setSaving(true)
    setError(null)
    try {
      const { error: rpcError } = await supabase.rpc('create_order_v3', {
        p_payload: {
          client_mode: form.client_mode,
          client_id: form.client_mode === 'existing' ? form.client_id : null,
          new_client_type: form.client_mode === 'new' ? form.new_client_type : null,
          new_client_first_name: form.client_mode === 'new' ? form.new_client_first_name.trim() || null : null,
          new_client_last_name: form.client_mode === 'new' ? form.new_client_last_name.trim() || null : null,
          new_client_company_name: form.client_mode === 'new' ? form.new_client_company_name.trim() || null : null,
          new_client_phone: form.client_mode === 'new' ? form.new_client_phone.trim() || null : null,
          new_client_email: form.client_mode === 'new' ? form.new_client_email.trim() || null : null,
          new_client_instagram: form.client_mode === 'new' ? form.new_client_instagram.trim() || null : null,
          source_id: form.client_mode === 'new' ? form.source_id || null : null,
          service_id: form.service_id,
          responsible_user_id: form.responsible_user_id,
          title: form.title.trim() || null,
          fulfilment_at: new Date(form.fulfilment_at).toISOString(),
          amount_gross: Number(form.amount_gross.replace(',', '.')),
          vat_rate: Number(form.vat_rate.replace(',', '.')),
          delivery_method: form.delivery_method,
          recipient_name: form.delivery_method === 'delivery' ? form.recipient_name.trim() : null,
          recipient_phone: form.delivery_method === 'delivery' ? form.recipient_phone.trim() : null,
          delivery_address: form.delivery_method === 'delivery' ? form.delivery_address.trim() : null,
          confirmation_status: form.confirmation_status,
          payment_status: form.payment_status,
          fulfilment_status: 'to_plan',
          notes: form.notes.trim() || null
        }
      })
      if (rpcError) throw rpcError
      setModalOpen(false)
      setNotice(t('orderCreatedStandalone'))
      await load()
    } catch (caught) {
      setError(errorMessage(caught, t('errorSaving')))
    }
    setSaving(false)
  }

  async function deleteOrder(order: OrderRow) {
    if (!supabase || !window.confirm(t('confirmDeleteOrder'))) return
    setDeletingId(order.id)
    setError(null)
    setNotice(null)
    const { error: rpcError } = await supabase.rpc('delete_order_v1', { p_order_id: order.id })
    if (rpcError) setError(errorMessage(rpcError, t('errorSaving')))
    else {
      setNotice(t('orderDeleted'))
      await load()
    }
    setDeletingId(null)
  }

  return <div className="module-stack">
    <section className="module-toolbar panel toolbar-wrap">
      <div className="toolbar-search"><span>⌕</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('search')} /></div>
      <button className="secondary-button" onClick={() => void load()}>{t('refresh')}</button>
      <button className="primary-button" onClick={openCreate}>＋ {t('createOrder')}</button>
    </section>
    {notice && <InlineNotice type="success">{notice}</InlineNotice>}
    {error && !modalOpen && <InlineNotice type="error">{error}</InlineNotice>}
    {loading ? <LoadingBlock /> : filtered.length === 0 ? <EmptyState title={t('ordersEmpty')} description={t('ordersEmptyDescription')} action={<button className="primary-button" onClick={openCreate}>{t('createOrder')}</button>} /> : <section className="data-card panel"><div className="table-scroll"><table className="data-table">
      <thead><tr><th>{t('number')}</th><th>{t('client')}</th><th>{t('service')}</th><th>{t('desiredDate')}</th><th>{t('confirmationStatus')}</th><th>{t('paymentStatus')}</th><th>{t('fulfilmentStatus')}</th><th>{t('amount')}</th><th>{t('actions')}</th></tr></thead>
      <tbody>{filtered.map((order) => <tr key={order.id}>
        <td><span className="record-title">{order.number}</span>{order.title && <div className="record-subtitle">{order.title}</div>}</td>
        <td>{clientDisplayName(order.clients)}</td>
        <td>{serviceName(order.services, language)}</td>
        <td>{formatDate(order.fulfilment_at, language, true)}</td>
        <td><StatusBadge tone={order.confirmation_status === 'confirmed' ? 'success' : order.confirmation_status === 'cancelled' ? 'danger' : 'neutral'}>{confirmationLabels[language][order.confirmation_status]}</StatusBadge></td>
        <td><StatusBadge tone={order.payment_status === 'paid' ? 'success' : order.payment_status === 'overdue' ? 'danger' : 'warning'}>{paymentLabels[language][order.payment_status]}</StatusBadge></td>
        <td><StatusBadge tone={order.fulfilment_status === 'completed' || order.fulfilment_status === 'delivered' ? 'success' : 'neutral'}>{fulfilmentLabels[language][order.fulfilment_status]}</StatusBadge></td>
        <td>{formatMoney(Number(order.amount_gross || 0), language)}</td>
        <td>{canDelete && <button className="table-action danger-text" disabled={deletingId === order.id} onClick={() => void deleteOrder(order)}>{t('deleteOrder')}</button>}</td>
      </tr>)}</tbody>
    </table></div></section>}

    <Modal open={modalOpen} title={t('createOrder')} subtitle={t('requiredFields')} onClose={() => setModalOpen(false)} wide footer={<><button className="secondary-button" onClick={() => setModalOpen(false)}>{t('cancel')}</button><button className="primary-button" disabled={saving} onClick={() => void save()}>{saving ? t('saving') : t('save')}</button></>}>
      {error && <InlineNotice type="error">{error}</InlineNotice>}
      <div className="segmented-control">
        <button className={form.client_mode === 'existing' ? 'active' : ''} onClick={() => update('client_mode', 'existing')}>{t('existingClient')}</button>
        <button className={form.client_mode === 'new' ? 'active' : ''} onClick={() => update('client_mode', 'new')}>{t('createNewClient')}</button>
      </div>
      <div className="form-grid">
        {form.client_mode === 'existing' ? <label className="field field-span-3"><span>{t('client')} *</span><select value={form.client_id} onChange={(event) => update('client_id', event.target.value)}><option value="">{t('chooseClient')}</option>{clients.map((client) => <option key={client.id} value={client.id}>{clientDisplayName(client)}</option>)}</select></label> : <>
          <label className="field"><span>{t('clientType')} *</span><select value={form.new_client_type} onChange={(event) => update('new_client_type', event.target.value as 'individual' | 'company')}><option value="individual">{t('individual')}</option><option value="company">{t('company')}</option></select></label>
          {form.new_client_type === 'company' ? <label className="field field-span-2"><span>{t('companyName')} *</span><input value={form.new_client_company_name} onChange={(event) => update('new_client_company_name', event.target.value)} /></label> : <><label className="field"><span>{t('firstName')} *</span><input value={form.new_client_first_name} onChange={(event) => update('new_client_first_name', event.target.value)} /></label><label className="field"><span>{t('lastName')}</span><input value={form.new_client_last_name} onChange={(event) => update('new_client_last_name', event.target.value)} /></label></>}
          <label className="field"><span>{t('phone')} *</span><input value={form.new_client_phone} onChange={(event) => update('new_client_phone', event.target.value)} /></label>
          <label className="field"><span>{t('email')}</span><input type="email" value={form.new_client_email} onChange={(event) => update('new_client_email', event.target.value)} /></label>
          <label className="field"><span>{t('instagram')}</span><input value={form.new_client_instagram} onChange={(event) => update('new_client_instagram', event.target.value)} /></label>
          <label className="field field-span-3"><span>{t('firstSource')}</span><select value={form.source_id} onChange={(event) => update('source_id', event.target.value)}><option value="">—</option>{sources.map((source) => <option key={source.id} value={source.id}>{sourceName(source, language)}</option>)}</select></label>
        </>}
        <label className="field"><span>{t('service')} *</span><select value={form.service_id} onChange={(event) => update('service_id', event.target.value)}><option value="">{t('chooseService')}</option>{services.map((service) => <option key={service.id} value={service.id}>{serviceName(service, language)}</option>)}</select></label>
        <label className="field"><span>{t('responsible')} *</span><select value={form.responsible_user_id} onChange={(event) => update('responsible_user_id', event.target.value)}><option value="">{t('chooseResponsible')}</option>{profiles.map((userProfile) => <option key={userProfile.id} value={userProfile.id}>{userProfile.full_name || userProfile.email}</option>)}</select></label>
        <label className="field"><span>{t('fulfilmentDateTime')} *</span><input type="datetime-local" value={form.fulfilment_at} onChange={(event) => update('fulfilment_at', event.target.value)} /></label>
        <label className="field field-span-2"><span>{t('orderTitle')}</span><input value={form.title} onChange={(event) => update('title', event.target.value)} /></label>
        <label className="field"><span>{t('orderAmountGross')} *</span><input inputMode="decimal" value={form.amount_gross} onChange={(event) => update('amount_gross', event.target.value)} /></label>
        <label className="field"><span>{t('vatRate')}</span><input inputMode="decimal" value={form.vat_rate} onChange={(event) => update('vat_rate', event.target.value)} /></label>
        <label className="field"><span>{t('deliveryMethod')}</span><select value={form.delivery_method} onChange={(event) => update('delivery_method', event.target.value as OrderForm['delivery_method'])}><option value="pickup">{t('pickup')}</option><option value="delivery">{t('delivery')}</option><option value="onsite">{t('onsite')}</option></select></label>
        {form.delivery_method === 'delivery' && <>
          <label className="field"><span>{t('recipientName')} *</span><input value={form.recipient_name} onChange={(event) => update('recipient_name', event.target.value)} /></label>
          <label className="field"><span>{t('recipientPhone')} *</span><input value={form.recipient_phone} onChange={(event) => update('recipient_phone', event.target.value)} /></label>
          <label className="field field-span-3"><span>{t('deliveryAddress')} *</span><input value={form.delivery_address} onChange={(event) => update('delivery_address', event.target.value)} /></label>
        </>}
        <label className="field"><span>{t('confirmationStatus')}</span><select value={form.confirmation_status} onChange={(event) => update('confirmation_status', event.target.value as OrderForm['confirmation_status'])}><option value="confirmed">{t('confirmed')}</option><option value="draft">{t('draft')}</option></select></label>
        <label className="field"><span>{t('paymentStatus')}</span><select value={form.payment_status} onChange={(event) => update('payment_status', event.target.value as OrderForm['payment_status'])}><option value="unpaid">{t('unpaid')}</option><option value="partially_paid">{t('partiallyPaid')}</option><option value="paid">{t('paid')}</option></select></label>
        <label className="field field-span-3"><span>{t('notes')}</span><textarea rows={4} value={form.notes} onChange={(event) => update('notes', event.target.value)} /></label>
      </div>
    </Modal>
  </div>
}
